import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Card, Row, Col, Typography, Space, Button, message, Image, Upload } from 'antd';
import { UploadOutlined, UserOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import type { RcFile } from 'antd/es/upload/interface';
import axios from 'axios';
import { useOnboardingStore } from '../stores/onboarding-store';

const { Title, Text } = Typography;

export interface PhotoSignatureUploadProps {
  photoFileId?: string;
  signatureFileId?: string;
  onPhotoChange: (fileId: string, uploadedAt: string) => void;
  onSignatureChange: (fileId: string, uploadedAt: string) => void;
  onPhotoRemove: () => void;
  onSignatureRemove: () => void;
}

const PhotoSignatureUpload: React.FC<PhotoSignatureUploadProps> = ({
  photoFileId,
  signatureFileId,
  onPhotoChange,
  onSignatureChange,
  onPhotoRemove,
  onSignatureRemove,
}) => {
  const { steps, currentStep, markStepCompleted } = useOnboardingStore();
  const [photoUploading, setPhotoUploading] = useState(false);
  const [signatureUploading, setSignatureUploading] = useState(false);
  const [photoUrl, setPhotoUrl] = useState<string>();
  const [signatureUrl, setSignatureUrl] = useState<string>();
  const hasMarkedComplete = useRef(false);
  
  // Auto-complete step when component mounts (since photo/signature are optional)
  // Or can be enhanced to require both files
  useEffect(() => {
    if (hasMarkedComplete.current) return;
    
    const currentStepData = steps[currentStep];
    if (currentStepData?.component === 'PhotoSignatureUpload') {
      // Auto-complete if optional, or check if files uploaded if required
      if (!currentStepData.isRequired || photoFileId || signatureFileId) {
        markStepCompleted(currentStep);
        hasMarkedComplete.current = true;
      }
    }
  }, [photoFileId, signatureFileId, currentStep, markStepCompleted, steps]);

  // Load preview URLs if file IDs exist
  useEffect(() => {
    if (photoFileId) {
      setPhotoUrl(`/api/files/preview/documents/${photoFileId}`);
    } else {
      setPhotoUrl(undefined);
    }
  }, [photoFileId]);

  useEffect(() => {
    if (signatureFileId) {
      setSignatureUrl(`/api/files/preview/documents/${signatureFileId}`);
    } else {
      setSignatureUrl(undefined);
    }
  }, [signatureFileId]);

  const handlePhotoUpload = useCallback(async (file: RcFile) => {
    setPhotoUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const metadata = {
        category: 'CUSTOMER_PHOTO',
        domain: 'CUSTOMER',
        documentType: 'PHOTO',
        description: 'Customer passport photo',
      };
      formData.append('metadata', JSON.stringify(metadata));

      const response = await axios.post('/api/files/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const fileId = response.data.id;
      const uploadedAt = new Date().toISOString();
      
      onPhotoChange(fileId, uploadedAt);
      message.success('Photo uploaded successfully');
    } catch (error) {
      console.error('Photo upload error:', error);
      message.error('Failed to upload photo');
    } finally {
      setPhotoUploading(false);
    }
    
    return false; // Prevent default upload
  }, [onPhotoChange]);

  const handleSignatureUpload = useCallback(async (file: RcFile) => {
    setSignatureUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const metadata = {
        category: 'CUSTOMER_SIGNATURE',
        domain: 'CUSTOMER',
        documentType: 'SIGNATURE',
        description: 'Customer signature',
      };
      formData.append('metadata', JSON.stringify(metadata));

      const response = await axios.post('/api/files/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const fileId = response.data.id;
      const uploadedAt = new Date().toISOString();
      
      onSignatureChange(fileId, uploadedAt);
      message.success('Signature uploaded successfully');
    } catch (error) {
      console.error('Signature upload error:', error);
      message.error('Failed to upload signature');
    } finally {
      setSignatureUploading(false);
    }
    
    return false; // Prevent default upload
  }, [onSignatureChange]);

  const handlePhotoRemove = () => {
    onPhotoRemove();
    message.info('Photo removed');
  };

  const handleSignatureRemove = () => {
    onSignatureRemove();
    message.info('Signature removed');
  };

  return (
    <div className="photo-signature-upload" style={{ marginTop: 24 }}>
      <Title level={4}>Customer Photo & Signature</Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
        Upload a passport-style photo and signature for the customer
      </Text>
      
      <Row gutter={24}>
        {/* Photo Upload */}
        <Col xs={24} md={12}>
          <Card
            title={
              <Space>
                <UserOutlined />
                <span>Passport Photo</span>
              </Space>
            }
            extra={
              photoFileId && (
                <Button
                  type="link"
                  danger
                  size="small"
                  icon={<DeleteOutlined />}
                  onClick={handlePhotoRemove}
                >
                  Remove
                </Button>
              )
            }
            style={{ height: '100%' }}
          >
            {photoUrl ? (
              <div style={{ textAlign: 'center' }}>
                <Image
                  src={photoUrl}
                  alt="Customer Photo"
                  style={{ maxWidth: '100%', maxHeight: 300, borderRadius: 8 }}
                  preview={true}
                  fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg=="
                />
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <UserOutlined style={{ fontSize: 64, color: '#d9d9d9' }} />
                <div style={{ marginTop: 16 }}>
                  <Upload
                    accept="image/*"
                    showUploadList={false}
                    beforeUpload={handlePhotoUpload}
                    disabled={photoUploading}
                  >
                    <Button
                      type="primary"
                      icon={<UploadOutlined />}
                      loading={photoUploading}
                    >
                      {photoUploading ? 'Uploading...' : 'Upload Photo'}
                    </Button>
                  </Upload>
                </div>
                <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
                  Recommended: Passport-style photo<br />
                  Formats: JPG, PNG (Max 5MB)
                </Text>
              </div>
            )}
          </Card>
        </Col>

        {/* Signature Upload */}
        <Col xs={24} md={12}>
          <Card
            title={
              <Space>
                <EditOutlined />
                <span>Signature</span>
              </Space>
            }
            extra={
              signatureFileId && (
                <Button
                  type="link"
                  danger
                  size="small"
                  icon={<DeleteOutlined />}
                  onClick={handleSignatureRemove}
                >
                  Remove
                </Button>
              )
            }
            style={{ height: '100%' }}
          >
            {signatureUrl ? (
              <div style={{ textAlign: 'center' }}>
                <Image
                  src={signatureUrl}
                  alt="Customer Signature"
                  style={{ maxWidth: '100%', maxHeight: 300, borderRadius: 8 }}
                  preview={true}
                  fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg=="
                />
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <EditOutlined style={{ fontSize: 64, color: '#d9d9d9' }} />
                <div style={{ marginTop: 16 }}>
                  <Upload
                    accept="image/*"
                    showUploadList={false}
                    beforeUpload={handleSignatureUpload}
                    disabled={signatureUploading}
                  >
                    <Button
                      type="primary"
                      icon={<UploadOutlined />}
                      loading={signatureUploading}
                    >
                      {signatureUploading ? 'Uploading...' : 'Upload Signature'}
                    </Button>
                  </Upload>
                </div>
                <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
                  Upload scanned or digital signature<br />
                  Formats: JPG, PNG (Max 2MB)
                </Text>
              </div>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default PhotoSignatureUpload;
